import React from 'react'
import type { TFunction } from 'react-i18next'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { Nil, User } from '@pbt/pbt-ui-components'
import { DateUtils, Utils } from '@pbt/pbt-ui-components'

import { PrescriptionItemState } from '~/constants/SOAPStates'
import { fetchPrescription } from '~/store/duck/prescriptions'
import { getPrescriptionStates } from '~/store/reducers/constants'
import { getUser } from '~/store/reducers/users'

export const ensureDrPrefix = (personString: string, t: TFunction): string => {
  const s = (personString ?? '').trim()
  if (!s) return s

  // Accept "Dr", "Dr.", "DR.", "dr" etc. (with or without whitespace after)
  if (/^dr\.?(\s|$)/i.test(s)) return personString

  const dr = t('Clients:TIMELINE.RX_STATUS_TRACKER.DR').trim()
  return `${dr} ${s}`
}

const getModificationDate = (prescription: {
  modificationDate?: string | Nil
  updatedAt?: string | Nil
}): string | undefined =>
  prescription.modificationDate ?? prescription.updatedAt ?? undefined

const extractPrescriptionDates = (prescription: {
  createdAt?: string | Nil
  creationDate?: string | Nil
  modificationDate?: string | Nil
  updatedAt?: string | Nil
}): {
  creationDate?: string | Nil
  modificationDate?: string | Nil
  updatedAt?: string | Nil
} => ({
  creationDate: prescription.creationDate ?? prescription.createdAt,
  modificationDate: prescription.modificationDate ?? prescription.updatedAt,
  updatedAt: prescription.updatedAt,
})

const shouldShowDoctorInfo = (
  enabled: boolean,
  stateName: string | Nil,
  actorId: string | Nil,
): boolean => {
  if (!enabled) return false

  const isExcludedState =
    stateName === PrescriptionItemState.PENDING_VET_APPROVAL

  if (isExcludedState) return false

  return !(stateName === PrescriptionItemState.APPROVED && !actorId)
}

const formatDoctorOnlyDisplay = (
  doctorStringRaw: string,
  doctorString: string,
): string | undefined => (doctorStringRaw ? doctorString : undefined)

interface TranslationData {
  actor: User | Nil
  doctor: User
  formattedDate: string
  status: string
}

const formatWithTranslation = (data: TranslationData, t: TFunction): string => {
  const { status, doctor, actor, formattedDate } = data

  const doctorString = ensureDrPrefix(Utils.getPersonString(doctor), t)
  const isOnBehalf = Boolean(actor)

  if (isOnBehalf) {
    const actorStringRaw = Utils.getPersonString(actor)
    const actorString = ensureDrPrefix(actorStringRaw, t)
    return t(
      'Clients:TIMELINE.RX_STATUS_TRACKER.STATUS_BY_SIGNER_ON_BEHALF_OF_DOCTOR_ON_DATE',
      {
        status,
        doctor: doctorString,
        signer: actorString,
        date: formattedDate,
      },
    )
  }

  return t('Clients:TIMELINE.RX_STATUS_TRACKER.STATUS_BY_DOCTOR_ON_DATE', {
    status,
    doctor: doctorString,
    date: formattedDate,
  })
}

/**
 * Generates a doctor info string with appropriate status prefix and optional "on behalf of" suffix.
 * Uses prescription state nameTranslation for status text.
 */
export const useDoctorInfoDisplay = ({
  doctor,
  showDoctorOnly = false,
  signerId,
  stateId,
  date,
  prescription,
  enabled = true,
}: {
  date?: string | Nil
  doctor: User | Nil
  enabled?: boolean
  prescription?: {
    createdAt?: string | Nil
    creationDate?: string | Nil
    id?: string
    modificationDate?: string | Nil
    updatedAt?: string | Nil
  }
  showDoctorOnly?: boolean
  signerId: string | Nil
  stateId?: string | Nil
}): string | undefined => {
  const { t } = useTranslation(['Common', 'Clients'])
  const dispatch = useDispatch()
  const prescriptionStates = useSelector(getPrescriptionStates)
  const signer = useSelector(getUser(signerId))
  const signerExists = Boolean(signer)

  const fetchAttemptedRef = React.useRef<string | null>(null)

  // Fetch prescription if signer is needed but not in Redux
  React.useEffect(() => {
    if (fetchAttemptedRef.current !== signerId) {
      fetchAttemptedRef.current = null
    }

    if (
      enabled &&
      signerId &&
      prescription?.id &&
      !signerExists &&
      signerId !== doctor?.id &&
      fetchAttemptedRef.current !== signerId
    ) {
      fetchAttemptedRef.current = signerId
      dispatch(fetchPrescription(prescription.id))
    }
  }, [enabled, signerId, prescription?.id, signerExists, doctor?.id, dispatch])

  const prescriptionState = prescriptionStates.find((s) => s.id === stateId)
  const stateName = Utils.getConstantName(stateId, prescriptionStates)
  const status = prescriptionState?.nameTranslation || stateName || ''

  if (!shouldShowDoctorInfo(enabled, stateName, signerId)) {
    return undefined
  }

  const doctorStringRaw = Utils.getPersonString(doctor)
  const doctorString = ensureDrPrefix(doctorStringRaw, t)

  const prescriptionDates = prescription
    ? extractPrescriptionDates(prescription)
    : undefined
  const dateToUse = prescriptionDates
    ? (getModificationDate(prescriptionDates) ?? date)
    : date
  const formattedDate = dateToUse ? DateUtils.formatDate(dateToUse) || '' : ''

  if (!doctor || showDoctorOnly) {
    return formatDoctorOnlyDisplay(doctorStringRaw, doctorString)
  }

  if (!status) {
    return undefined
  }

  const actorHasValidName = signer && (signer.firstName || signer.lastName)
  const actor =
    signerId && signerId !== doctor.id && actorHasValidName ? signer : null

  return formatWithTranslation({ status, doctor, actor, formattedDate }, t)
}
